import React, { Component, memo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import { ModalStyled } from '../../../../theme/customizedStyleComponents';
import { ThemeContext } from '../../../../theme/ThemeProvider';
import { fetchFamilyMembers, fetchFamilyOrders } from '../../../../services/clinicPortalServices/familyService';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class FamilyViewPopup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            members: [],
            orders: [],
            startDate: moment().subtract(30, 'days').toDate(),
            endDate: moment().toDate(),
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true,
                resizable: true
            },
            memberColumns: [
                {
                    headerName: 'First Name',
                    field: 'first_name',
                    minWidth: 150
                },
                {
                    headerName: 'Last Name',
                    field: 'last_name',
                    minWidth: 150
                },
                {
                    headerName: 'MRN',
                    field: 'mrn',
                    minWidth: 120
                },
                {
                    headerName: 'Relationship',
                    field: 'family_relationship',
                    minWidth: 150
                },
                {
                    headerName: 'Date of Birth',
                    field: 'date_of_birth',
                    minWidth: 150,
                    valueGetter: (params) => {
                        return params.data.date_of_birth
                            ? moment(params.data.date_of_birth).format('MM/DD/YYYY')
                            : '';
                    }
                },
                {
                    headerName: 'Gender',
                    field: 'gender',
                    minWidth: 100
                }
            ],
            orderColumns: [
                {
                    headerName: 'Patient',
                    minWidth: 180,
                    valueGetter: (params) => {
                        return params.data.patient_id
                            ? `${params.data.patient_id.first_name} ${params.data.patient_id.last_name} (${params.data.patient_id.family_relationship})`
                            : '';
                    }
                },
                {
                    headerName: 'Requisition',
                    field: 'lab_order_id',
                    minWidth: 150
                },
                {
                    headerName: 'Test',
                    field: 'test_id.name',
                    minWidth: 150
                },
                {
                    headerName: 'Sample',
                    field: 'test_info.sample',
                    minWidth: 120
                },
                {
                    headerName: 'Status',
                    field: 'status',
                    minWidth: 120
                },
                {
                    headerName: 'Collected Date',
                    field: 'test_info.collected',
                    minWidth: 180,
                    valueGetter: (params) => {
                        return params.data.test_info?.collected
                            ? moment(params.data.test_info.collected, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A')
                            : '';
                    }
                },
                {
                    headerName: 'Released Date',
                    field: 'results.released',
                    minWidth: 180,
                    valueGetter: (params) => {
                        return params.data.results?.released
                            ? moment(params.data.results.released, 'YYYYMMDDHHmmss').format('MM/DD/YYYY hh:mm A')
                            : '';
                    }
                }
            ]
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && this.props.show !== prevProps.show) {
            this.loadData();
        }
    }

    loadData = () => {
        // Load family members
        fetchFamilyMembers(this.props.data._id).then((res) => {
            this.setState({ members: res.data });
        });

        // Load family orders
        this.loadOrders();
    };

    loadOrders = () => {
        fetchFamilyOrders(this.props.data._id, {
            startDate: moment(this.state.startDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            endDate: moment(this.state.endDate).endOf('day').format('YYYY-MM-DD HH:mm:ss')
        }).then((res) => {
            this.setState({ orders: res.data });
        });
    };

    handleDateChange = (date, type) => {
        this.setState({ [type]: date }, () => {
            this.loadOrders();
        });
    };

    render() {
        return (
            <ModalStyled
                size="xl"
                centered
                show={this.props.show}
                onHide={this.props.handleClose}
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Family Details - {this.props.data?.family_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <strong>Family ID:</strong> {this.props.data?.family_id}
                        </div>
                        <div className="col-md-6">
                            <strong>Total Members:</strong> {this.state.members.length}
                        </div>
                    </div>

                    <hr />

                    <h5 className="mb-3">Family Members</h5>
                    <ThemeContext.Consumer>
                        {({ themeName }) => (
                            <div style={{ height: '250px', width: '100%', marginBottom: '30px' }}>
                                <AgGridReact
                                    columnDefs={this.state.memberColumns}
                                    defaultColDef={this.state.defaultColDef}
                                    rowData={this.state.members}
                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                />
                            </div>
                        )}
                    </ThemeContext.Consumer>

                    <hr />

                    <h5 className="mb-3">Family Orders</h5>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label>Start Date</label>
                            <MobileDatePicker
                                value={this.state.startDate}
                                onChange={(date) => this.handleDateChange(date.$d, 'startDate')}
                                renderInput={(props) => (
                                    <TextField
                                        {...props}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton edge="end">
                                                        <EventIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-4">
                            <label>End Date</label>
                            <MobileDatePicker
                                value={this.state.endDate}
                                onChange={(date) => this.handleDateChange(date.$d, 'endDate')}
                                renderInput={(props) => (
                                    <TextField
                                        {...props}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton edge="end">
                                                        <EventIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <ThemeContext.Consumer>
                        {({ themeName }) => (
                            <div style={{ height: '350px', width: '100%' }}>
                                <AgGridReact
                                    columnDefs={this.state.orderColumns}
                                    defaultColDef={this.state.defaultColDef}
                                    rowData={this.state.orders}
                                    pagination={true}
                                    paginationPageSize={20}
                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                />
                            </div>
                        )}
                    </ThemeContext.Consumer>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.props.handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </ModalStyled>
        );
    }
}